import React, { useState, useEffect } from "react";
import EditModelForm from "./EditModelForm";
import AddModelForm from "./AddModelForm";
import ColorVarianceModal from "./ColorVarianceModal";
import editIcon from "../assets/icons/EditIcon.svg";
import colorIcon from "../assets/icons/ColorVarianceIcon.svg";




const ModelSearch = ({ refreshTrigger }) => {
  const [models, setModels] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModel, setSelectedModel] = useState(null);
  const [showEditModelForm, setShowEditModelForm] = useState(false);
  const [showAddModelForm, setShowAddModelForm] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchModels = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch("/api/models", {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add auth token if you have one
          // 'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        // Add credentials if needed for CORS
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        throw new Error(`Expected JSON, got: ${text.slice(0, 100)}`);
      }

      const data = await res.json();
      console.log('✅ Models fetched:', data.length);
      setModels(data);
      
    } catch (err) {
      console.error("❌ Error fetching models:", err);
      
      // Provide user-friendly error messages
      let errorMessage = "Failed to load models. ";
      
      if (err.message.includes('Failed to fetch')) {
        errorMessage += "Cannot connect to server. Please check:\n" +
          "1. Backend server is running\n" +
          "2. API URL is correct: " + url + "\n" +
          "3. CORS is properly configured";
      } else if (err.message.includes('DNS')) {
        errorMessage += "Server domain not found. Check your API_BASE configuration.";
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [refreshTrigger]);

  const filteredModels = models.filter((model) =>
    (model.asset1_name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
    (model.asset1_code?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );

  const handleAddModel = () => setShowAddModelForm(true);

  const handleEditModel = (model) => {
    setSelectedModel(model);
    setShowEditModelForm(true);
  };

  const handleSaveModel = () => {
    fetchModels();
    setShowEditModelForm(false);
  };

  const handleModelAdded = () => {
    fetchModels();
    setShowAddModelForm(false);
  };

  const handleColorVariance = (model) => {
    setSelectedModel(model);
    setShowColorModal(true);
  };

  const closeColorModal = () => {
    setShowColorModal(false);
    setSelectedModel(null);
    fetchModels();
  };

  return (
    <div className="user-list-container">
      <h2>Model List</h2>

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '15px',
          margin: '10px 0',
          backgroundColor: '#fee',
          border: '1px solid #c33',
          borderRadius: '4px',
          color: '#c33',
          whiteSpace: 'pre-line'
        }}>
          <strong>⚠️ Error:</strong><br />
          {error}
          <button 
            onClick={fetchModels}
            style={{
              marginTop: '10px',
              padding: '5px 10px',
              cursor: 'pointer'
            }}
          >
            🔄 Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ padding: '10px', textAlign: 'center' }}>
          Loading models...
        </div>
      )}

      <div className="search-section">
        <label>Search Models:</label>
        <input
          type="text"
          placeholder="Search by name or code"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className="button-container">
        <button 
          className="add-model-button" 
          onClick={handleAddModel}
          disabled={loading}
        >
          + Add Model
        </button>
      </div>

      <div className="UserList-container">
        <table>
          <thead>
            <tr>
              <th>Model Name</th>
              <th>Model Code</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredModels.length > 0 ? (
              filteredModels.map((model) => (
                <tr key={model.asset1_id}>
                  <td>{model.asset1_name}</td>
                  <td>{model.asset1_code}</td>
                  <td className="action-cell">
                    <div className="action-wrapper">
                      <button
                        className="icon-action-btn"
                        onClick={() => handleEditModel(model)}
                        title="Edit Model"
                      >
                        <img
                          src={editIcon}
                          alt="Edit"
                          className="table-icon"
                        />
                      </button>

                      <button
                        className="icon-action-btn"
                        onClick={() => handleColorVariance(model)}
                        title="Color Variance"
                      >
                        <img
                          src={colorIcon}
                          alt="Color Variance"
                          className="table-icon"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: "center" }}>
                  {loading ? "Loading..." : "No models match your search."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showEditModelForm && selectedModel && (
        <EditModelForm
          model={selectedModel}
          onClose={() => setShowEditModelForm(false)}
          onSave={handleSaveModel}
        />
      )}

      {showAddModelForm && (
        <AddModelForm
          onClose={() => setShowAddModelForm(false)}
          onmodelAdded={handleModelAdded}
        />
      )}

      {showColorModal && selectedModel && (
        <ColorVarianceModal
          model={selectedModel}
          isOpen={showColorModal}
          onClose={closeColorModal}
        />
      )}
    </div>
  );
};

export default ModelSearch;